import React, { useRef, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { FiArrowRight } from "react-icons/fi";
import Button from "./Button";

gsap.registerPlugin(ScrollTrigger);

const Navbar = () => {
  const containerRef = useRef(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuLinesRef = useRef([]);
  const mobileMenuRef = useRef(null);
  const mobileLinksRef = useRef([]);
  const location = useLocation();

  const links = [
    { name: "Home", href: "/" },
    { name: "About", href: "/about" },
    { name: "Services", href: "/services" },
    { name: "Work", href: "/works" },
    { name: "Contact", href: "/contact" },
  ];

  // Scroll behavior logic removed per user request (always stay visible)
  useEffect(() => {
    // Keep this empty or remove if not needed, 
    // but we can use it to add a background color on scroll if desired.
    // For now, we are skipping the hiding logic.
  }, []);

  // Initial Entry Animation
  useGSAP(() => {
    gsap.from(containerRef.current, {
      y: -100,
      opacity: 0,
      duration: 1.2,
      ease: "power4.out",
      delay: 0.5,
    });
  }, { scope: containerRef });

  // Mobile Menu Animation
  useEffect(() => {
    if (isMenuOpen) {
      // Open Menu
      gsap.to(mobileMenuRef.current, {
        height: "100vh",
        opacity: 1,
        duration: 0.8,
        ease: "power3.inOut",
        display: "flex",
      });

      gsap.fromTo(mobileLinksRef.current,
        { y: 50, opacity: 0 },
        { y: 0, opacity: 1, stagger: 0.1, duration: 0.6, delay: 0.3, ease: "power3.out" }
      );

      // Animate Burger to X
      gsap.to(menuLinesRef.current[0], { rotate: 45, y: 8, duration: 0.3 });
      gsap.to(menuLinesRef.current[1], { opacity: 0, duration: 0.3 });
      gsap.to(menuLinesRef.current[2], { rotate: -45, y: -8, duration: 0.3 });

    } else {
      // Close Menu
      gsap.to(mobileMenuRef.current, {
        height: 0,
        opacity: 0,
        duration: 0.6,
        ease: "power3.inOut",
        onComplete: () => gsap.set(mobileMenuRef.current, { display: "none" })
      });

      // Animate X back to Burger
      gsap.to(menuLinesRef.current[0], { rotate: 0, y: 0, duration: 0.3 });
      gsap.to(menuLinesRef.current[1], { opacity: 1, duration: 0.3 });
      gsap.to(menuLinesRef.current[2], { rotate: 0, y: 0, duration: 0.3 });
    }
  }, [isMenuOpen]);

  return (
    <>
      <nav ref={containerRef} className={`fixed top-0 left-0 right-0 flex justify-center px-0 w-full pointer-events-none ${isMenuOpen ? "z-[1001]" : "z-[999]"}`}>
        <div className={`pointer-events-auto relative px-4 py-2 md:px-12 md:py-4 flex items-center justify-between backdrop-blur-xl border-b border-white/10 shadow-[0_2px_10px_0_rgba(0,0,0,0.05)] w-full transition-all duration-300 ${isMenuOpen ? "bg-transparent border-transparent shadow-none" : "bg-white/[0.03]"}`}>

          {/* Logo Section */}
          <Link to="/" className="flex items-center gap-3 group/logo">
            <img
              src="/images/logo.webp"
              alt="Xpin Media Logo"
              className="h-8 md:h-12 w-auto object-contain transition-transform duration-500 group-hover/logo:scale-110"
            />
          </Link>

          {/* Desktop Links */}
          <div
            className="hidden md:flex items-center gap-2 relative"
          >
            {links.map((link, i) => {
              const isActive = location.pathname === link.href || (location.pathname === '/' && location.hash === link.href.replace('/', ''));

              return (
                <Link
                  key={i}
                  to={link.href}
                  className={`relative px-6 py-2 rounded-xl text-[11px] font-bold tracking-wide uppercase transition-all duration-300 z-10 ${isActive
                    ? "text-white bg-[#f04e23]"
                    : "text-gray-300 hover:text-white"
                    }`}
                >
                  {link.name}
                </Link>
              );
            })}
          </div>

          {/* CTA & Burger */}
          <div className="flex items-center gap-3">
            <Link to="/contact" className="hidden sm:flex items-center">
              <Button text="Let's Talk" size="sm" />
            </Link>

            {/* Mobile Burger */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden w-10 h-10 flex flex-col items-center justify-center gap-[5px] bg-transparent relative z-[1001]"
            >
              <div ref={el => menuLinesRef.current[0] = el} className="w-6 h-[2px] bg-white origin-center"></div>
              <div ref={el => menuLinesRef.current[1] = el} className="w-6 h-[2px] bg-white"></div>
              <div ref={el => menuLinesRef.current[2] = el} className="w-6 h-[2px] bg-white origin-center"></div>
            </button>
          </div>

        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        ref={mobileMenuRef}
        className="fixed inset-0 bg-black/98 backdrop-blur-2xl z-[1000] hidden flex-col items-center justify-center pointer-events-auto"
        style={{ height: 0, opacity: 0 }}
      >
        <div className="flex flex-col items-center gap-6 pt-20">
          {links.map((link, i) => {
            const isActive = location.pathname === link.href || (location.pathname === '/' && location.hash === link.href.replace('/', ''));
            return (
              <Link
                key={i}
                to={link.href}
                ref={el => mobileLinksRef.current[i] = el}
                className={`text-4xl md:text-6xl font-bold transition-all duration-300 transform hover:scale-110 ${isActive
                  ? "text-white"
                  : "text-transparent bg-clip-text bg-gradient-to-b from-white to-white/50 hover:to-cyan-400"
                  }`}
                onClick={() => setIsMenuOpen(false)}
              >
                {link.name}
              </Link>
            );
          })}
          <div ref={el => mobileLinksRef.current[links.length] = el} className="mt-8">
            <Link to="/contact" onClick={() => setIsMenuOpen(false)} className="inline-block">
              <Button text="Let's Talk" size="sm" />
            </Link>
          </div>
        </div>
      </div>

    </>
  )
}

export default Navbar;
